import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ScanEye, Zap, Play, Youtube } from "lucide-react";
import { HiLink } from "react-icons/hi";

export default function ProductCard({ item }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // --- HELPER: Extract YouTube Video ID ---
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}` : null;
  };

  // 1. Construct the media list dynamically
  const mediaList = [];

  // Priority 1: Uploaded Video
  if (+item.isVideo === 1 && item.video_link) {
    mediaList.push({ type: "video", src: item.video_link });
  }

  // Priority 2: YouTube Video
  if (item.yt_link) {
    const embedUrl = getYouTubeEmbedUrl(item.yt_link);
    if (embedUrl) {
      mediaList.push({ type: "youtube", src: embedUrl });
    }
  }

  // Priority 3: Images
  if (Array.isArray(item.images) && item.images.length > 0) {
    item.images.forEach((img) => mediaList.push({ type: "image", src: img }));
  } else if (item.image) {
    mediaList.push({ type: "image", src: item.image });
  }

  // 2. Navigation Handlers
  const nextSlide = (e) => {
    e.preventDefault();
    setCurrentSlide((prev) => (prev === mediaList.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e) => {
    e.preventDefault();
    setCurrentSlide((prev) => (prev === 0 ? mediaList.length - 1 : prev - 1));
  };

  return (
    <div className="group relative bg-[#0f172a] h-full flex flex-col border border-slate-800 hover:border-[#22d3ee]/50 transition-colors duration-300">
      
      {/* --- MEDIA SECTION --- */}
      <div className="relative overflow-hidden aspect-[4/5] bg-[#020617] border-b border-[#22d3ee]/20">
        
        {/* Tech Badge */}
        <div className="absolute top-0 left-0 z-20">
           <div className="bg-[#22d3ee] text-[#020617] text-[10px] font-bold px-3 py-1 font-mono tracking-tighter inline-flex items-center gap-1">
              V.1.0
           </div>
        </div>

        {/* Scanline Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_2px,3px_100%] pointer-events-none opacity-30"></div>

        {/* --- MEDIA RENDERER --- */}
        <div className="w-full h-full relative">
          {mediaList.map((media, index) => (
            <div
              key={index}
              className={`absolute inset-0 w-full h-full transition-all duration-500 ease-in-out ${
                index === currentSlide ? "opacity-100 z-0" : "opacity-0 z-0"
              }`}
            >
              {media.type === "video" ? (
                <div className="relative w-full h-full">
                    <video
                    src={media.src}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    autoPlay
                    loop
                    muted
                    playsInline
                    />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/50 p-3 rounded-full border border-[#22d3ee]/50 backdrop-blur-sm">
                        <Play size={16} className="text-[#22d3ee] fill-[#22d3ee]" />
                    </div>
                </div>
              ) : media.type === "youtube" ? (
                <div className="relative w-full h-full bg-black">
                    <iframe
                        src={media.src}
                        title="YouTube video player"
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity pointer-events-none"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                    <div className="absolute top-2 right-2 bg-red-600/80 p-1.5 rounded-sm backdrop-blur-sm z-20">
                        <Youtube size={16} className="text-white fill-white" />
                    </div>
                </div>
              ) : (
                <img
                  src={media.src}
                  alt={item.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 ease-out"
                />
              )}
            </div>
          ))}
        </div>

        {/* --- SLIDER CONTROLS --- */}
        {mediaList.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-0 top-0 bottom-0 z-20 px-2 text-[#22d3ee] opacity-0 group-hover:opacity-100 transition-all hover:bg-[#22d3ee]/10 flex items-center justify-center"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-0 bottom-0 z-20 px-2 text-[#22d3ee] opacity-0 group-hover:opacity-100 transition-all hover:bg-[#22d3ee]/10 flex items-center justify-center"
            >
              <ChevronRight size={24} />
            </button>
            
            <div className="absolute top-2 right-2 z-20 flex gap-1">
                {mediaList.map((_, idx) => (
                    <div 
                        key={idx} 
                        className={`w-1 h-1 rounded-sm ${idx === currentSlide ? 'bg-[#22d3ee] shadow-[0_0_5px_#22d3ee]' : 'bg-slate-700'}`} 
                    />
                ))}
            </div>
          </>
        )}

        {/* --- ACTION BUTTONS --- */}
        <div className="absolute bottom-0 left-0 w-full flex translate-y-full group-hover:translate-y-0 transition-transform duration-300 z-30 font-mono text-[10px] md:text-xs font-bold tracking-widest">
            <Link
                to={`/product/${item.id}`} 
                className="w-1/2 bg-[#020617]/90 backdrop-blur-md text-[#22d3ee] py-4 border-t border-[#22d3ee] hover:bg-[#22d3ee] hover:text-[#020617] flex items-center justify-center gap-2 transition-colors group/btn"
            >
                <ScanEye size={14}/> VIEW
            </Link>

            <Link
                to={item.pay_link}
                className="w-1/2 bg-[#020617]/90 backdrop-blur-md text-[#facc15] py-4 border-t border-[#facc15] hover:bg-[#facc15] hover:text-[#020617] flex items-center justify-center gap-2 transition-colors"
            >
               <Zap size={14}/> {item.price ? 'ACQUIRE' : 'GET'}
            </Link>
        </div>
      </div>

      {/* --- INFO SECTION --- */}
      <div className="p-4 flex flex-col flex-grow relative overflow-hidden">
         {/* Decorative Corner */}
         <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#22d3ee]/30"></div>

        {/* Live Link Button */}
        {item.live_link && (
            <a 
                href={item.live_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="group/live flex items-center justify-center gap-2 bg-[#facc15]/5 border border-[#facc15]/50 text-[#facc15] font-bold py-1.5 px-3 mb-3 hover:bg-[#facc15] hover:text-black transition-all duration-300 w-full uppercase tracking-wider text-[10px] relative overflow-hidden"
            >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/live:translate-y-0 transition-transform duration-300 skew-y-12"></div>
                <HiLink size={14} className="relative z-10"/>
                <span className="relative z-10">Live Preview</span>
            </a>
        )}

         {/* Title */}
         <div className="mb-2">
            <h3 className="text-white font-bold text-sm leading-tight font-sans truncate group-hover:text-[#22d3ee] transition-colors">
                {item.title}
            </h3>
         </div>

         {/* Main Tech (Protocol Tag) */}
         {item.main_tech_name && (
            <div className="mb-3">
               <span className="inline-flex items-center text-[10px] font-mono text-[#22d3ee]/80 border-l-2 border-[#22d3ee]/50 pl-2">
                 CORE: <span className="text-white ml-1 uppercase">{item.main_tech_name}</span>
               </span>
            </div>
         )}

         {/* Tech Stack (Data Chips) */}
         {item.tech_stack && (
            <div className="flex flex-wrap gap-1.5 mb-4">
               {item.tech_stack.split(",").map((tech, i) => (
                   <span 
                    key={i} 
                    className="text-[9px] font-medium text-slate-400 bg-slate-800/50 px-2 py-0.5 rounded-sm border border-slate-700/50 hover:border-[#22d3ee]/30 hover:text-[#22d3ee] transition-colors cursor-default whitespace-nowrap"
                   >
                       {tech.trim()}
                   </span>
               ))}
            </div>
         )}
         
         {/* Footer (Price & ID) */}
         <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-800/80">
             <p className="text-[#22d3ee] font-mono text-sm tracking-tight drop-shadow-[0_0_2px_rgba(34,211,238,0.5)]">
                {Number(item.price) > 0 ? `₹${item.price}` : 'FREE_ACCESS'}
             </p>
             <span className="text-[9px] text-slate-600 font-mono uppercase tracking-widest">
                #{item.id?.toString().padStart(4, '0')}
             </span>
         </div>

      </div>
    </div>
  );
}